import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Inbox, CheckCircle2, Mail, Trash2, RotateCcw, Reply, Send } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "react-router-dom";

const SUBJECTS = [
  { key: "", label: "Tous sujets" },
  { key: "general", label: "Question générale" },
  { key: "support", label: "Support" },
  { key: "partner", label: "Partenariat" },
  { key: "bug", label: "Bug" },
  { key: "billing", label: "Facturation" },
  { key: "suggestion", label: "Suggestion" },
  { key: "press", label: "Presse" },
  { key: "other", label: "Autre" },
];

const SOURCE_LABELS = {
  "app_mobile:app_ios": "App iOS",
  "app_mobile:app_android": "App Android",
  app_web: "Site web",
};

export default function AdminContact() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, handled: 0 });
  const [subjectKey, setSubjectKey] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/contact", { params: { subject_key: subjectKey, status } })
      .then((r) => {
        setItems(r.data.items || []);
        setCounts(r.data.counts || { new: 0, handled: 0 });
      })
      .catch(() => toast.error("Impossible de charger les messages"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [subjectKey, status]);

  const toggleStatus = async (m) => {
    const next = m.status === "handled" ? "new" : "handled";
    try {
      await api.post(`/admin/contact/${m.id}/status`, { status: next });
      toast.success(next === "handled" ? "Marqué traité" : "Remis en non traité");
      load();
    } catch {
      toast.error("Erreur de mise à jour");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer définitivement le message de ${m.name} ?`)) return;
    try {
      await api.delete(`/admin/contact/${m.id}`);
      toast.success("Message supprimé");
      load();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const sendReply = async (m) => {
    if (replyText.trim().length < 2) return toast.error("Écrivez votre réponse d'abord.");
    setSendingReply(true);
    try {
      await api.post(`/admin/contact/${m.id}/reply`, { reply: replyText.trim() });
      toast.success(`Réponse envoyée par email à ${m.email}`);
      setReplyingId(null);
      setReplyText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi de la réponse");
    } finally {
      setSendingReply(false);
    }
  };

  const mailto = (m) =>
    `mailto:${m.email}?subject=${encodeURIComponent(`Re: ${m.subject || "Votre message DoctoSpa"}`)}`;

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-10" data-testid="admin-contact-page">
      <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" /> Admin dashboard
      </Link>

      <div className="eyebrow text-secondary">Admin</div>
      <h1 className="mt-2 font-display text-3xl lg:text-4xl font-black tracking-tight">
        Boîte de contact
      </h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="contact-kpis">
        <KPI icon={Inbox} label="Non traités" value={counts.new} color="text-amber-600" />
        <KPI icon={CheckCircle2} label="Traités" value={counts.handled} color="text-emerald-600" />
        <KPI icon={Mail} label="Total" value={counts.new + counts.handled} color="text-blue-600" />
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="flex rounded-full border border-black/10 p-1 bg-white" data-testid="status-filter">
          {[
            { v: "", label: "Tous" },
            { v: "new", label: "Non traités" },
            { v: "handled", label: "Traités" },
          ].map((s) => (
            <button
              key={s.v}
              onClick={() => setStatus(s.v)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                status === s.v ? "bg-slate-950 text-white" : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`status-filter-${s.v || "all"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <select
          value={subjectKey}
          onChange={(e) => setSubjectKey(e.target.value)}
          className="h-9 rounded-full border border-black/10 bg-white px-4 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="subject-filter"
        >
          {SUBJECTS.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className="mt-8 text-sm text-muted-foreground">Chargement…</p>
      ) : items.length === 0 ? (
        <Card className="mt-8 p-10 text-center text-sm text-muted-foreground" data-testid="contact-empty">
          Aucun message pour ces filtres.
        </Card>
      ) : (
        <div className="mt-6 space-y-3" data-testid="contact-list">
          {items.map((m) => (
            <Card key={m.id} className={`p-5 ${m.status === "handled" ? "opacity-70" : ""}`} data-testid={`contact-msg-${m.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{m.name}</span>
                    <a href={`mailto:${m.email}`} className="text-xs font-mono text-primary hover:underline">{m.email}</a>
                    {m.phone && <span className="text-xs font-mono text-muted-foreground">{m.phone}</span>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="rounded-full text-[10px]">
                      {SUBJECTS.find((s) => s.key === m.subject_key)?.label || m.subject_key}
                    </Badge>
                    <Badge variant="outline" className="rounded-full text-[10px]">
                      {SOURCE_LABELS[m.source] || m.source}
                    </Badge>
                    <Badge className={`rounded-full text-[10px] ${m.status === "handled" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {m.status === "handled" ? "Traité" : "Non traité"}
                    </Badge>
                    <span className="text-[11px] text-muted-foreground">
                      {new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    variant={replyingId === m.id ? "default" : "outline"}
                    className="rounded-full h-8 text-xs"
                    onClick={() => { setReplyingId(replyingId === m.id ? null : m.id); setReplyText(""); }}
                    data-testid={`btn-quick-reply-${m.id}`}
                  >
                    <Reply className="h-3.5 w-3.5 mr-1.5" /> Répondre
                  </Button>
                  <Button
                    size="sm"
                    variant={m.status === "handled" ? "outline" : "default"}
                    className="rounded-full h-8 text-xs"
                    onClick={() => toggleStatus(m)}
                    data-testid={`btn-toggle-status-${m.id}`}
                  >
                    {m.status === "handled" ? (
                      <><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Rouvrir</>
                    ) : (
                      <><CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Traité</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-full h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => remove(m)}
                    data-testid={`btn-delete-${m.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {m.subject && <p className="mt-3 text-sm font-semibold">{m.subject}</p>}
              <p className="mt-1.5 text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{m.message}</p>
              {m.reply_text && (
                <div className="mt-3 rounded-xl bg-blue-50 border border-blue-100 p-3" data-testid={`sent-reply-${m.id}`}>
                  <p className="text-[11px] font-semibold text-blue-700">
                    ✓ Réponse envoyée{m.replied_at ? ` le ${new Date(m.replied_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                  </p>
                  <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">{m.reply_text}</p>
                </div>
              )}
              {replyingId === m.id && (
                <div className="mt-3 rounded-xl border border-black/10 bg-white p-3" data-testid={`reply-box-${m.id}`}>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={`Votre réponse à ${m.name}… (envoyée par email à ${m.email})`}
                    rows={4}
                    maxLength={5000}
                    autoFocus
                    className="rounded-lg"
                    data-testid={`reply-textarea-${m.id}`}
                  />
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <p className="text-[11px] text-muted-foreground">Le client pourra répondre directement à l'email reçu.</p>
                    <div className="flex items-center gap-2">
                      <a href={mailto(m)} className="text-[11px] text-muted-foreground hover:text-foreground underline" data-testid={`link-mailto-${m.id}`}>
                        via mon client mail
                      </a>
                      <Button
                        size="sm"
                        className="rounded-full h-8 text-xs"
                        disabled={sendingReply}
                        onClick={() => sendReply(m)}
                        data-testid={`btn-send-reply-${m.id}`}
                      >
                        <Send className="h-3.5 w-3.5 mr-1.5" />
                        {sendingReply ? "Envoi…" : "Envoyer la réponse"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className={`h-4 w-4 ${color}`} />
        {label}
      </div>
      <div className="mt-1 font-display font-black tracking-tight text-2xl">{value}</div>
    </Card>
  );
}
